import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NotificationsService } from '../../services/notifications.service';

@Component({
  selector: 'app-notifications-list',
  templateUrl: './notifications-list.component.html',
  styleUrls: ['./notifications-list.component.scss']
})
export class NotificationsListComponent implements OnInit {
  notifications: any[] = [];
  loading: boolean = false;
  currentPage: number = 1;
  pageSize: number = 50;
  itemsPerPageOptions: number[] = [10,25,50,100];
  totalItems: number = 0; // se actualizará tras cada carga
  
  constructor(
    private router: Router,
    private notificationsService: NotificationsService
  ) { }

  ngOnInit(): void {
    this.loadNotifications();
  }

  loadNotifications(): void {
    this.loading = true;
    const offset = (this.currentPage - 1) * this.pageSize;
    this.notificationsService.getAllNotifications(offset, this.pageSize)
      .subscribe({
        next: (data) => {
          this.notifications = data;
          // Sin total real desde backend: asumimos totalItems >= endItem mientras haya page llena
          const received = data.length;
          this.totalItems = (received < this.pageSize) ? offset + received : offset + received + this.pageSize; // heurística provisoria
          this.loading = false;
        },
        error: (error) => {
          console.error('Error al cargar notificaciones:', error);
          this.loading = false;
          // Mostrar datos de ejemplo en caso de error
          this.notifications = [
            {
              id: 1,
              title: 'Notificación importante',
              message: 'Este es un mensaje importante para todos los usuarios',
              created_at: new Date().toISOString(),
              is_read: false,
              importance: 'alta',
              notification_type: 'info',
              is_broadcast: true,
              broadcast_filter: 'all'
            }
          ];
          this.totalItems = this.notifications.length;
        }
      });
  }

  get totalPages(): number {
    if (this.totalItems === 0) return 1;
    return Math.max(1, Math.ceil(this.totalItems / this.pageSize));
  }

  get startItem(): number {
    if (this.totalItems === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endItem(): number {
    const theoreticalEnd = this.currentPage * this.pageSize;
    return Math.min(theoreticalEnd, this.totalItems);
  }

  getPageNumbers(): number[] {
    const pagesToShow = 5;
    const total = this.totalPages;
    let start = Math.max(1, this.currentPage - Math.floor(pagesToShow / 2));
    let end = start + pagesToShow - 1;
    if (end > total) {
      end = total;
      start = Math.max(1, end - pagesToShow + 1);
    }
    const pages: number[] = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;
    this.currentPage = page;
    this.loadNotifications();
  }

  onPageSizeChange(): void {
    this.currentPage = 1;
    this.loadNotifications();
  }

  createNotification(): void {
    this.router.navigate(['/notificaciones/crear']);
  }

  getRecipientText(notification: any): string {
    if (notification.is_broadcast) {
      if (notification.broadcast_filter === 'all') {
        return 'Todos los usuarios';
      } else if (notification.broadcast_filter?.startsWith('role:')) {
        const role = notification.broadcast_filter.split(':')[1];
        return `Rol: ${this.getRoleDisplayName(role)}`;
      } else if (notification.broadcast_filter?.startsWith('users:')) {
        const userCount = notification.broadcast_filter.split(':')[1].split(',').length;
        return `${userCount} usuarios específicos`;
      }
    }
    return 'Usuario individual';
  }

  getRoleDisplayName(role: string): string {
    const roleNames: any = {
      'admin': 'Administrador',
      'teacher': 'Profesor',
      'analyst': 'Analista',
      'customer': 'Cliente'
    };
    return roleNames[role] || role;
  }

  getImportanceClass(importance: string): string {
    switch (importance) {
      case 'alta': return 'bg-danger';
      case 'media': return 'bg-warning';
      case 'baja': return 'bg-info';
      default: return 'bg-secondary';
    }
  }

  getTypeClass(type: string): string {
    switch (type) {
      case 'info': return 'bg-info';
      case 'warning': return 'bg-warning';
      case 'success': return 'bg-success';
      case 'error': return 'bg-danger';
      default: return 'bg-secondary';
    }
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}
